import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Stethoscope, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

const AdminLogin = () => {
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(password);
      toast.success("Welcome back, admin!");
      navigate("/admin", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="admin-login-page"
      className="min-h-screen grid lg:grid-cols-2 bg-background"
    >
      <div className="hidden lg:flex relative bg-primary text-primary-foreground p-12 flex-col justify-between overflow-hidden">
        <div className="absolute inset-0 opacity-20"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 30%, white 0%, transparent 35%), radial-gradient(circle at 80% 70%, white 0%, transparent 35%)",
          }}
        />
        <div className="relative">
          <div className="flex items-center gap-2.5">
            <span className="w-11 h-11 rounded-2xl bg-white/15 backdrop-blur grid place-items-center">
              <Stethoscope className="w-5 h-5" />
            </span>
            <span className="font-heading font-bold text-xl">
              Ayushman Distributors
            </span>
          </div>
        </div>
        <div className="relative">
          <h1 className="font-heading font-bold text-4xl xl:text-5xl tracking-tight leading-tight">
            Admin
            <br />
            Console.
          </h1>
          <p className="mt-5 text-base text-white/85 leading-relaxed max-w-md">
            Manage your Pediarange product catalogue and review contact form
            submissions in one place.
          </p>
        </div>
        <p className="relative text-xs text-white/60">
          © {new Date().getFullYear()} Ayushman Distributors · Pediarange®
        </p>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <form
          onSubmit={onSubmit}
          data-testid="admin-login-form"
          className="w-full max-w-md bg-white border border-border rounded-3xl p-8 sm:p-10 soft-shadow"
        >
          <div className="flex items-center gap-2.5 lg:hidden mb-6">
            <span className="w-10 h-10 rounded-2xl bg-primary text-primary-foreground grid place-items-center">
              <Stethoscope className="w-5 h-5" />
            </span>
            <span className="font-heading font-bold text-lg">Ayushman</span>
          </div>

          <h2 className="font-heading font-bold text-3xl tracking-tight text-foreground">
            Sign in
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Enter your admin password to continue.
          </p>

          <div className="mt-7">
            <Label htmlFor="password" className="text-sm font-semibold">
              Password
            </Label>
            <div className="relative mt-2">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                id="password"
                type={showPwd ? "text" : "password"}
                required
                autoFocus
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-12 rounded-xl pl-10 pr-10 text-base"
                placeholder="Enter password"
                data-testid="admin-login-password"
              />
              <button
                type="button"
                onClick={() => setShowPwd((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                data-testid="admin-login-toggle-password"
                aria-label="Toggle password visibility"
              >
                {showPwd ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>

          <Button
            type="submit"
            disabled={submitting || !password}
            data-testid="admin-login-submit"
            className="mt-7 w-full rounded-full bg-primary hover:bg-primary/90 h-12 font-semibold"
          >
            {submitting ? "Signing in…" : "Sign in"}
          </Button>

          <p className="mt-6 text-center text-xs text-muted-foreground">
            Authorised access only.{" "}
            <a href="/" className="text-primary font-medium hover:underline">
              Back to website
            </a>
          </p>
        </form>
      </div>
    </div>
  );
};

export default AdminLogin;
